/**
 * The 5-minute quick start: the badge, the backlog, the customer line, the one action
 * with the reply, closed reasons. Five slides reused by reference from the course (no
 * copied text), then a 5-item check that differs from the final quiz. Pass: 4 of 5 (80 %).
 * The "How it works" figure of w1 and the queue row figure of q2 show here through the
 * reused slides (round 3, 12 Oct 2026). Ease round (13 Oct 2026): q2 says where a new
 * person starts ("Ready to quote"), r3 says what to do when a case does not go through.
 */

import type { QuickStart } from "@/features/training/types";

export const QUICK_START: QuickStart = {
  minutes: 5,
  steps: [
    { module: "welcome", slide: "w1" },
    { module: "queue", slide: "q2" },
    { module: "customer", slide: "c2" },
    { module: "reply", slide: "r3" },
    { module: "status", slide: "s2" },
  ],
  check: {
    id: "qs-check",
    kind: "quiz",
    required: true,
    questions: [
      {
        id: "qs-send",
        topic: "shadow",
        stem: "Who sends the reply to the customer?",
        options: [
          {
            id: "qs-send-a",
            text: "I2Q, when you click Copy the reply.",
            correct: false,
            fb: "No. Copy the reply puts the text on your clipboard, nothing more.",
          },
          {
            id: "qs-send-b",
            text: "You, from Outlook.",
            correct: true,
            fb: "Right. I2Q prepares, you send.",
          },
          {
            id: "qs-send-c",
            text: "The shared mailbox, on its own.",
            correct: false,
            fb: "No. Nothing goes out on its own. I2Q runs in shadow mode.",
          },
          {
            id: "qs-send-d",
            text: "F&O, with the quotation.",
            correct: false,
            fb: "No. I2Q writes nothing to F&O.",
          },
        ],
      },
      {
        id: "qs-backlog",
        topic: "queue",
        stem: "The queue looks short. Where is the rest?",
        options: [
          {
            id: "qs-backlog-a",
            text: "There is no rest. The queue is complete.",
            correct: false,
            fb: "No. The default view shows only today. The rest is under the backlog chips.",
          },
          {
            id: "qs-backlog-b",
            text: "In the Closed tab.",
            correct: false,
            fb: "No. Closed cases are filed. Your open work is behind the backlog chips.",
          },
          {
            id: "qs-backlog-c",
            text: "Behind the chips `open, all dates` and `open older than 7 days`.",
            correct: true,
            fb: "Right. Open them once a day.",
          },
          {
            id: "qs-backlog-d",
            text: "In your Outlook inbox.",
            correct: false,
            fb: "No. I2Q reads the shared mailbox. Your open cases are all in the queue.",
          },
        ],
      },
      {
        id: "qs-customer",
        topic: "customer",
        stem: "When do you confirm a suggested customer?",
        options: [
          {
            id: "qs-customer-a",
            text: "When `Why` on the customer line shows evidence.",
            correct: true,
            fb: "Right. No evidence, no confirmation. Click `Not them` and search instead.",
          },
          {
            id: "qs-customer-b",
            text: "Always, to see the prices.",
            correct: false,
            fb: "No. A wrong customer gives wrong prices and teaches I2Q the wrong thing.",
          },
          {
            id: "qs-customer-c",
            text: "When the name looks familiar.",
            correct: false,
            fb: "No. The same name can exist in two countries. Check the evidence.",
          },
          {
            id: "qs-customer-d",
            text: "Never. I2Q confirms it.",
            correct: false,
            fb: "No. You confirm the customer. I2Q only suggests.",
          },
        ],
      },
      {
        id: "qs-attached",
        topic: "reply",
        stem: "The reply says: attached quotation. What do you remove?",
        options: [
          {
            id: "qs-attached-a",
            text: "Nothing. Attach the PDF.",
            correct: false,
            fb: "No. The lines belong in the mail text.",
          },
          {
            id: "qs-attached-b",
            text: "The whole reply.",
            correct: false,
            fb: "No. Most of the reply is useful. Correct the one sentence.",
          },
          {
            id: "qs-attached-c",
            text: "The lines.",
            correct: false,
            fb: "No. The lines are what the customer needs. Keep them in the mail.",
          },
          {
            id: "qs-attached-d",
            text: "That sentence. The lines go into the mail itself.",
            correct: true,
            fb: "Right. Remove it with `Edit text` under `More`, then `Copy the reply`.",
          },
        ],
      },
      {
        id: "qs-closed",
        topic: "status",
        stem: "A case is Closed. Did the customer get a reply?",
        options: [
          {
            id: "qs-closed-a",
            text: "Yes. Closed means the customer got a reply.",
            correct: false,
            fb: "No. Closed is filed in I2Q only.",
          },
          {
            id: "qs-closed-b",
            text: "Not always. Check the thread. Reopen the case if it still needs a reply.",
            correct: true,
            fb: "Right. Read the reason in the caption of the big button. Then check the thread. Reopen sits under More.",
          },
          {
            id: "qs-closed-c",
            text: "Yes, I2Q sent the reply.",
            correct: false,
            fb: "No. I2Q never sends.",
          },
          {
            id: "qs-closed-d",
            text: "No. I2Q deletes closed cases.",
            correct: false,
            fb: "No. I2Q deletes nothing. The case stays in the Closed tab.",
          },
        ],
      },
    ],
    topicHints: {
      shadow: { module: "welcome", text: "Look again at shadow mode: I2Q prepares, you send." },
      queue: {
        module: "queue",
        text: "Look again at the default view: open the chip open, all dates every day. Start with the tab Ready to quote: you can finish these cases at once.",
      },
      customer: {
        module: "customer",
        text: "Look again at Confirm the customer: only confirm with evidence.",
      },
      lines: {
        module: "lines",
        text: "Look again at Check the draft: orange box pick, blue question mark confirm, green check matched. The lines that need you are on top. Compare each row with the customer's words. The big button confirms every suggested row in one click. When a case does not go through, read the sentence next to the big button and click it.",
      },
      prices: {
        module: "prices",
        text: "Look again at Prices: decide the rows, your manager approves in the checks.",
      },
      reply: {
        module: "reply",
        text: "Look again at the reply: remove the attachment sentence before you copy.",
      },
      status: {
        module: "status",
        text: "Look again at Closed: it is not a reply.",
      },
      feedback: {
        module: "extras",
        text: "Look again at Feedback: it does not change the case.",
      },
    },
  },
};

/** The two buttons on the quick start end screen. */
export const QUICK_START_END = {
  continueText: "Continue with the full training (20 minutes)",
  backText: "Back to the queue",
} as const;
